// skillgenpro-frontend/src/error-framework/errorRegistry.js
//
// Static registry mapping an error `code` to how it should be presented and
// recovered from. This is the single source of truth for "what does the
// user see and what can they do" — see the Phase 2 design doc's Recovery
// Matrix for the reasoning behind each entry.
//
// A registry entry is intentionally plain data (no JSX, no React) so it can
// be imported from both React components and plain services (fetchWrapper,
// axiosInterceptor) without pulling React into non-component code.
//
// Fields:
//   category     - taxonomy category (matches backend's `category`)
//   title        - short, human title shown as the error heading
//   description  - the full contextual explanation; a function of the
//                  resolved context so copy can reference e.g. what kind of
//                  work is being protected ("your interview", "your code")
//   actions      - ordered list of action keys (see recoveryEngine.js for
//                  what each key does); the first is the primary action
//   retryable    - can the user retry this themselves
//   autoRetry    - should the recovery engine retry automatically
//   severity     - 'low' | 'medium' | 'high' | 'critical' (affects styling)

export const DEFAULT_ENTRY = {
  category: 'UNKNOWN',
  title: "Something didn't work",
  description: () =>
    'Something unexpected happened. Our team has been notified — please try again in a moment.',
  actions: ['retry', 'support', 'dashboard'],
  retryable: true,
  autoRetry: false,
  severity: 'medium',
};

export const REGISTRY = {
  // --- Network / transport --------------------------------------------
  NETWORK_OFFLINE: {
    category: 'NETWORK',
    title: "You're offline",
    description: () =>
      "It looks like your internet connection dropped. We'll pick up right where you left off once you're back online.",
    actions: ['reconnect', 'dashboard'],
    retryable: true,
    autoRetry: true,
    severity: 'medium',
  },
  NETWORK_TIMEOUT: {
    category: 'NETWORK',
    title: 'This is taking longer than expected',
    description: () =>
      'The request is still trying — this can happen on a slow connection. You can keep waiting or retry now.',
    actions: ['retry', 'dashboard'],
    retryable: true,
    autoRetry: true,
    severity: 'low',
  },
  NETWORK_UNKNOWN: {
    category: 'NETWORK',
    title: "We couldn't reach the server",
    description: () =>
      'Something interrupted the connection. Please check your internet and try again.',
    actions: ['retry', 'support'],
    retryable: true,
    autoRetry: false,
    severity: 'medium',
  },

  // --- Auth --------------------------------------------------------------
  SESSION_EXPIRED: {
    category: 'AUTHENTICATION',
    title: 'Your session has expired',
    description: () => 'Please log in again to continue.',
    actions: ['login'],
    retryable: false,
    autoRetry: false,
    severity: 'medium',
  },
  PERMISSION_DENIED: {
    category: 'AUTHORIZATION',
    title: "You don't have access to this",
    description: () =>
      "Your account role doesn't include access to this feature. Reach out to your admin if you think this is wrong.",
    actions: ['dashboard', 'support'],
    retryable: false,
    autoRetry: false,
    severity: 'medium',
  },

  // --- Payment (Razorpay) --------------------------------------------
  PAYMENT_ORDER_CREATE_FAILED: {
    category: 'PAYMENT',
    title: "We couldn't start your payment",
    description: () =>
      "Nothing has been charged. Please try again — if this keeps happening, contact support and we'll get it sorted.",
    actions: ['retry', 'support'],
    retryable: true,
    autoRetry: false,
    severity: 'high',
  },
  PAYMENT_SIGNATURE_INVALID: {
    category: 'PAYMENT',
    title: "We couldn't confirm this payment",
    description: () =>
      "We weren't able to verify this payment with Razorpay. If money was deducted from your account, it will auto-refund within 5-7 business days. If you'd rather not wait, contact support with your error ID below.",
    actions: ['support', 'copyErrorId', 'dashboard'],
    retryable: true,
    autoRetry: false,
    severity: 'critical',
  },
  PAYMENT_SDK_LOAD_FAILED: {
    category: 'PAYMENT',
    title: "Payment couldn't load",
    description: () =>
      'This is usually a connection hiccup. Refreshing the page should fix it.',
    actions: ['refresh', 'support'],
    retryable: true,
    autoRetry: true,
    severity: 'medium',
  },
  PAYMENT_PROVIDER_UNREACHABLE: {
    category: 'PAYMENT',
    title: 'Razorpay is temporarily unreachable',
    description: () =>
      'Your plan selection is saved. This is usually brief — try again in a moment.',
    actions: ['retry', 'support'],
    retryable: true,
    autoRetry: true,
    severity: 'high',
  },
  PAYMENT_STORE_FAILED: {
    category: 'PAYMENT',
    title: "We couldn't confirm your payment",
    description: () =>
      "Your payment went through, but we ran into an issue recording it. This is not lost — our team is automatically notified and will apply it shortly. Contact support with your error ID if you'd rather not wait.",
    actions: ['support', 'copyErrorId', 'dashboard'],
    retryable: false,
    autoRetry: false,
    severity: 'critical',
  },

  // --- AI generation ---------------------------------------------------
  AI_QUOTA_EXCEEDED: {
    category: 'AI',
    title: "We're at capacity right now",
    description: (ctx) =>
      `Your ${ctx && ctx.aiSubject ? ctx.aiSubject : 'request'} is saved. We're automatically retrying — this usually resolves within a minute, no need to resend.`,
    actions: ['autoRetryIndicator', 'dashboard'],
    retryable: true,
    autoRetry: true,
    severity: 'low',
  },
  AI_GENERATION_FAILED: {
    category: 'AI',
    title: "We're currently unable to generate AI feedback",
    description: (ctx) =>
      `Your ${ctx && ctx.aiSubject ? ctx.aiSubject : 'work'} has been saved successfully. AI evaluation will be available once the service resumes — you don't need to redo anything.`,
    actions: ['retry', 'dashboard'],
    retryable: true,
    autoRetry: false,
    severity: 'medium',
  },
  AI_PROVIDER_TIMEOUT: {
    category: 'AI',
    title: 'Still working on this',
    description: () =>
      "This is taking longer than expected but hasn't failed. You can keep waiting or check back shortly.",
    actions: ['autoRetryIndicator', 'dashboard'],
    retryable: true,
    autoRetry: true,
    severity: 'low',
  },

  // --- Fallback ----------------------------------------------------------
  UNKNOWN_ERROR: DEFAULT_ENTRY,
};

/**
 * Look up a registry entry by code, falling back to a category-shaped
 * generic entry, then to DEFAULT_ENTRY. Never returns undefined.
 */
export function getRegistryEntry(code, category) {
  if (code && REGISTRY[code]) return REGISTRY[code];
  if (category) {
    return {
      ...DEFAULT_ENTRY,
      category,
    };
  }
  return DEFAULT_ENTRY;
}
